import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const MarkStackCompleteModal = ({ isOpen, onClose, onConfirm, quotes, isSaving }) => {
  const [selectedQuoteId, setSelectedQuoteId] = useState(null);
  const [confirmed, setConfirmed] = useState(false);

  if (!isOpen) return null;

  const availableQuotes = quotes?.filter(q => q?.price != null || q?.vendor?.name);

  const handleConfirm = () => {
    if (!selectedQuoteId) return;
    const selected = availableQuotes?.find(q => q?.id === selectedQuoteId);
    onConfirm(selected);
    setConfirmed(false);
    setSelectedQuoteId(null);
  };

  const handleClose = () => {
    setSelectedQuoteId(null);
    setConfirmed(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
        style={{ border: '1.5px solid #fde8d8' }}
      >
        {/* Header */}
        <div
          className="px-6 py-5"
          style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B, #E0528A)' }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/20 border border-white/30 flex items-center justify-center">
                <Icon name="CheckCircle" size={20} color="white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Mark Stack Complete</h2>
                <p className="text-white/80 text-xs mt-0.5">Select the option you purchased</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
            >
              <Icon name="X" size={16} color="white" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          <p className="text-sm text-gray-600 mb-4">
            Which option did you end up purchasing? This will mark the stack as completed and record your final choice.
          </p>

          {availableQuotes?.length === 0 ? (
            <div className="text-center py-8">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
                style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
              >
                <Icon name="Inbox" size={22} color="white" />
              </div>
              <p className="text-gray-500 text-sm">No options available in this stack yet.</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {availableQuotes?.map((quote) => {
                const isSelected = selectedQuoteId === quote?.id;
                return (
                  <button
                    key={quote?.id}
                    onClick={() => setSelectedQuoteId(quote?.id)}
                    className="w-full flex items-center gap-3 p-3.5 rounded-xl border-2 text-left transition-all"
                    style={{
                      borderColor: isSelected ? '#F58A54' : '#f3f4f6',
                      background: isSelected ? '#fff7f0' : 'white',
                    }}
                  >
                    {/* Selection indicator */}
                    <div
                      className="w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all"
                      style={{
                        borderColor: isSelected ? '#F58A54' : '#d1d5db',
                        background: isSelected ? '#F58A54' : 'white',
                      }}
                    >
                      {isSelected && <Icon name="Check" size={11} color="white" />}
                    </div>

                    {/* Vendor logo / initials */}
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 text-white text-sm font-bold"
                      style={{ background: isSelected ? 'linear-gradient(135deg, #F58A54, #F06F6B)' : 'linear-gradient(135deg, #e5e7eb, #d1d5db)' }}
                    >
                      {quote?.vendor?.logo ? (
                        <img src={quote?.vendor?.logo} alt={quote?.vendor?.logoAlt || quote?.vendor?.name} className="w-full h-full object-cover rounded-lg" />
                      ) : (
                        <span style={{ color: isSelected ? 'white' : '#6b7280' }}>
                          {quote?.vendor?.name?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 text-sm truncate">{quote?.vendor?.name || 'Unknown Vendor'}</div>
                      <div className="flex items-center gap-2 mt-0.5">
                        {quote?.price != null && (
                          <span className="text-xs font-medium" style={{ color: '#F58A54' }}>
                            ${quote?.price?.toLocaleString()}
                          </span>
                        )}
                        {quote?.vendor?.rating && (
                          <span className="flex items-center gap-0.5 text-xs text-gray-400">
                            <Icon name="Star" size={10} style={{ color: '#f59e0b' }} />
                            {quote?.vendor?.rating}
                          </span>
                        )}
                        {quote?.isOnlineResult && (
                          <span className="text-xs px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 font-medium">Online</span>
                        )}
                      </div>
                    </div>

                    {isSelected && (
                      <div className="flex-shrink-0">
                        <span
                          className="text-xs font-semibold px-2 py-1 rounded-full"
                          style={{ background: '#fff0e6', color: '#F58A54' }}
                        >
                          Selected
                        </span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 flex items-center gap-3">
          <button
            onClick={handleClose}
            className="flex-1 py-2.5 rounded-xl border text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
            style={{ borderColor: '#e5e7eb' }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedQuoteId || isSaving}
            className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={{ background: selectedQuoteId ? 'linear-gradient(135deg, #F58A54, #F06F6B)' : '#d1d5db' }}
          >
            {isSaving ? (
              <>
                <Icon name="Loader" size={14} color="white" className="animate-spin" />
                Completing...
              </>
            ) : (
              <>
                <Icon name="CheckCircle" size={14} color="white" />
                Complete Stack
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MarkStackCompleteModal;
